import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { AdminConsoleService } from '../services/admin-console.service';
import { environment } from '../../environments/environment';
import { PermissionService } from '../services/permission.service';
import { PathwaysPermission } from '../models/permissions.enum';

function redirectToSsoLogin(auth: AuthService, returnUrl?: string): false {
  auth.redirectToSsoLogin(returnUrl);
  return false;
}

export const authGuard: CanActivateFn = async (_route, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (environment.ssoEnabled) {
    const ready = await auth.ensureSsoSessionReady();
    if (ready && auth.isLoggedIn()) {
      return true;
    }
    return redirectToSsoLogin(auth, state.url || '/chat');
  }

  if (auth.isLoggedIn()) {
    return true;
  }
  return router.createUrlTree(['/login'], {
    queryParams: { returnUrl: state.url || '/chat' },
  });
};

/** PAT-203 — `/admin` requires sign-in and Pathways-AI Admin role (`auth.isAdmin()`). */
export const adminGuard: CanActivateFn = async (_route, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (environment.ssoEnabled) {
    const ready = await auth.ensureSsoSessionReady();
    if (!ready) {
      return redirectToSsoLogin(auth, state.url || '/admin');
    }
    if (!auth.isLoggedIn()) {
      return redirectToSsoLogin(auth, state.url || '/admin');
    }
    return auth.isAdmin() ? true : router.createUrlTree(['/chat']);
  }

  if (!auth.isLoggedIn()) {
    return router.createUrlTree(['/login'], {
      queryParams: { returnUrl: state.url || '/admin' },
    });
  }
  return auth.isAdmin() ? true : router.createUrlTree(['/chat']);
};

/**
 * Login route: with SSO, unauthenticated users are sent to the IdP; authenticated users skip the form.
 */
export const loginGuard: CanActivateFn = async () => {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (environment.ssoEnabled) {
    const ready = await auth.ensureSsoSessionReady();
    if (ready && auth.isLoggedIn()) {
      return router.createUrlTree(['/chat']);
    }
    return redirectToSsoLogin(auth);
  }

  if (auth.isLoggedIn()) {
    return router.createUrlTree(['/chat']);
  }

  return true;
};

/**
 * Permission guard factory — gates a route on a specific Pathways-AI permission code.
 *
 * Usage in routes:
 *   canActivate: [authGuard, permissionGuard(PathwaysPermission.MasterSkillsInventory)]
 *
 * The user must be logged in (authGuard) AND hold the given permission code.
 * When the permission is missing, the user is redirected to /chat.
 *
 * Note: authGuard should always precede this guard so SSO is resolved first.
 */
export function permissionGuard(requiredPermission: PathwaysPermission): CanActivateFn {
  return async (_route, _state) => {
    const auth = inject(AuthService);
    const perm = inject(PermissionService);
    const router = inject(Router);

    if (environment.ssoEnabled) {
      const ready = await auth.ensureSsoSessionReady();
      if (!ready || !auth.isLoggedIn()) {
        return redirectToSsoLogin(auth);
      }
    } else if (!auth.isLoggedIn()) {
      return router.createUrlTree(['/login']);
    }

    return perm.hasPermission(requiredPermission) ? true : router.createUrlTree(['/chat']);
  };
}

/** PAT-636 — `/admin/system` requires admin console access plus a granted maintenance row. */
export const maintenanceAccessGuard: CanActivateFn = async () => {
  const access = inject(AdminConsoleService);
  const router = inject(Router);
  const allowed = await access.loadMaintenanceAccess();
  return allowed ? true : router.createUrlTree(['/admin/overview']);
};
